import React, { useEffect, useCallback } from "react";
import { nanoid } from "nanoid";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
} from "@material-ui/core";
import Zoom from "@material-ui/core/Zoom";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import { Message } from "components/shared";
import { slugify } from "utils/string-helpers";
import useNavigate from "hooks/useNavigate";
import { useContextMenu } from "react-contexify";
import { TableHeader } from "components/Widgets/Tables/common/components";
import useSorting from "components/Widgets/Tables/common/hooks/useSorting";
import TypographyWithTooltip from "components/shared/TypographyWithTooltip/TypographyWithTooltip";
import getCellFormat from "components/Widgets/Tables/TableA/utils/getCellFormat";
import PropTypes from "prop-types";

const TABLE_CONTEXT_MENU_ID = "TABLE_CONTEXT_MENU_ID";

const useStyles = makeStyles((theme) => ({
  row: {
    textDecoration: "none",
    "& td": {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: theme.customStyles.tableRow.padding,
      "&:not(last-child)": {
        borderLeft: `1px solid ${theme.palette.grey.medium}`,
      },
    },
  },
  linkRow: {
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      boxShadow: theme.palette.boxShadow.default,
      backgroundColor: `${theme.palette.primary.main}40`,
    },
    "& td": {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: theme.customStyles.tableRow.padding,
      "&:first-child": {
        maxWidth: theme.customStyles.nthChild.maxWidth,
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow,
      },
      "&:nth-child(2)": {
        maxWidth: theme.customStyles.nthChild.maxWidth,
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow,
      },
      "&:nth-child(3)": {
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow,
      },
      "&:not(last-child)": {
        borderLeft: `1px solid ${theme.palette.grey.medium}`,
      },
    },
  },
  title: {
    fontSize: theme.customStyles.tableRow.fontSize,
  },
  negative: {
    color: theme.palette.error.main,
    userSelect: "none",
  },
  transparent: {
    backgroundColor: "transparent",
  },
}));

const SearchScreenTable = ({ state, setTableExportData }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { show } = useContextMenu();
  const { sortBy, sortOrder, getColumnSorting, onColumnSort } = useSorting();
  const { data: response, isLoading, isError, error } = state;

  const openInNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getMappedColumns = useCallback(() => {
    if (!response) return [];
    return response.header.map((item, index) => ({
      index: index + 1,
      key: item ? slugify(item) : "index",
      title: item.replace(/_/g, ' '),
      isSortable: true,
    }));
  }, [response]);

  const getMappedRows = useCallback(() => {
    if (!response) return [];
    const columns = getMappedColumns();
    return response.data.map((row) =>
      row.reduce(
        (result, next, index) => {
          const key = columns[index]?.key || "stocklink" || "index";
          return { ...result, [key]: next };
        },
        { uniqueKey: nanoid(6) }
      )
    );
  }, [response, getMappedColumns]);

  useEffect(() => {
    if (response) {
      const columns = getMappedColumns();
      const rows = [...getMappedRows()];
      setTableExportData({
        title: "Search Results",
        columns,
        rows,
      });
    }
  }, [response, getMappedColumns, getMappedRows]);

  if (isError) {
    return <Message type="error" text={error} />;
  }

  if (isLoading || !response) {
    return <LoadingSpinner />;
  }

  if (!isLoading && response.header.length < 1) {
    return <Message type="info" text="No data avaible" />;
  }

  function handleContextMenu(e, link) {
    const baseUrl = window.location.origin;
    show(e, {
      id: TABLE_CONTEXT_MENU_ID,
      props: {
        tabHref: `${baseUrl}${link}`,
      },
    });
  }

  const columns = getMappedColumns();
  const rows = getMappedRows();

  const renderRow = (row) => {
    return columns.map((column) => {
      if (
        column.key === "timeserieslinkid" ||
        column.key === "timeserieslinktype"
      ) {
        return null;
      }

      if (column.key === "stocklink") {
        return null;
      }
      const { value, classList } = getCellFormat(row[column.key]);
      const key = `${column.key}-${row.uniqueKey}`;
      return (
        <TableCell key={key}>
          <TypographyWithTooltip
            variant="subtitle1"
            className={classes.title}
            gutterBottom
            tooltipProps={{
              value,
              placement: "right",
              TransitionComponent: Zoom,
              arrow: true,
            }}
          >
            <span className={classList.map((c) => classes[c]).join(" ")}>
              {value}
            </span>
          </TypographyWithTooltip>
        </TableCell>
      );
    });
  };

  const renderBody = () => {
    const sortKey = sortBy || "index";
    const sortedRows = rows.sort(getColumnSorting(sortKey, sortOrder));

    const rowElements = sortedRows.map((row) => {
      if (row.timeserieslinkid) {
        return (
          <TableRow
            onClick={() => navigate(`/canned/${row.timeserieslinkid}`)}
            onContextMenu={(e) =>
              handleContextMenu(e, `/canned/${row.timeserieslinkid}`)
            }
            key={row.uniqueKey}
            className={classes.linkRow}
          >
            {renderRow(row)}
          </TableRow>
        );
      } else if (row.stocklink) {
        return (
          <TableRow
            onDoubleClick={() => openInNewTab(row.stocklink)}
            key={row.uniqueKey}
            className={classes.linkRow}
          >
            {renderRow(row)}
          </TableRow>
        );
      }
      return (
        <TableRow key={row.uniqueKey} className={classes.row}>
          {renderRow(row)}
        </TableRow>
      );
    });

    return <>{rowElements}</>;
  };

  return (
    <Table>
      <TableHeader columns={columns} onColumnSort={onColumnSort} />
      <TableBody>{renderBody()}</TableBody>
    </Table>
  );
};

SearchScreenTable.propTypes = {
  state: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),
  forwardRef: PropTypes.shape({}),
};

SearchScreenTable.propTypes = {
  state: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }).isRequired,
  }).isRequired,
  setTableExportData: PropTypes.func.isRequired,
};

export default SearchScreenTable;
